import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  health, deviceStatus, serverStatus, byUrgency, countStatuses, buildIncidents, groupByCompany,
  timeAgo, timeAgoEpoch, storageColor, diskInfo, formatStorage,
  TICKET_STATUS_LABEL, TICKET_ACTIVE, ticketLevel, ticketSla,
} from "./dashboardHelpers.js";
import { Section, Chip, LiveClock, LiveIndicator, CardGrid } from "./DashboardParts.jsx";
import { LastBackupLine, BackupJobList } from "./DeviceCards.jsx";
import ClientCard from "./ClientCard.jsx";
import ColumnsBoard, { useTicketList, segStyle, segBtn } from "./ColumnsTab.jsx";

// ══════════════════════════════════════════════════════
//  Onglet Tour de contrôle — fenêtre principale, pensée pour un grand écran.
//
//  Fixe en haut : 3 tuiles (UDM · NAS · Serveurs) + la liste « À traiter maintenant ».
//  En dessous, une grande zone qui change selon l'écran choisi :
//     Vue globale · UDM · NAS · Serveurs · Tickets          (écrans « TV », sans défilement)
//     Colonnes · Par client                                 (vues de bureau, anciennes présentations)
//  « Lecture auto » fait défiler les 5 écrans TV ; « Mode TV » passe en plein écran
//  et agrandit tout selon la taille de l'écran.
// ══════════════════════════════════════════════════════

const SCREENS = [
  { id: "globale",  label: "Vue globale", tv: true },
  { id: "udm",      label: "UDM",         tv: true },
  { id: "nas",      label: "NAS",         tv: true },
  { id: "serveurs", label: "Serveurs",    tv: true },
  { id: "tickets",  label: "Tickets",     tv: true },
  { id: "colonnes", label: "Colonnes",    tv: false },
  { id: "clients",  label: "Par client",  tv: false },
];
const TV_IDS = SCREENS.filter(s => s.tv).map(s => s.id);
const ROTATION_MS = 15_000;
const STORAGE_KEY = "deskora.controle.screen";

const lvlKey = (s) => ({ CRITICAL: "crit", WARNING: "warn", OK: "ok" }[s] || "ok");

function readScreen() {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    return SCREENS.some(s => s.id === v) ? v : "globale";
  } catch { return "globale"; }
}

// ── Petits blocs ─────────────────────────────────────
function Tag({ level, children }) {
  return <span className={`ctl-tag ${lvlKey(level)}`}>{children ?? health(level).label}</span>;
}

/** Tuile carrée : gros chiffre = nombre d'appareils à surveiller. */
function Tile({ icon, title, statuses, worstLines, caption, active, onClick }) {
  const c = countStatuses(statuses);
  const total = statuses.length;
  const problems = c.WARNING + c.CRITICAL;
  const level = c.CRITICAL ? "CRITICAL" : c.WARNING ? "WARNING" : "OK";
  const pct = (n) => (total ? `${(n / total) * 100}%` : "0%");
  return (
    <button type="button" className={`ctl-tile ${lvlKey(level)}${active ? " sel" : ""}`} onClick={onClick}>
      <h3><i className={`bi ${icon}`} />{title}</h3>
      <div className="ctl-big">
        <b>{total ? problems : "—"}</b>
        <span>{total === 0 ? "aucun appareil" : problems ? <>{caption}<br />sur {total}</> : <>tout est OK<br />sur {total}</>}</span>
      </div>
      <div className="ctl-bar3">
        <i style={{ width: pct(c.OK), background: "var(--success)" }} />
        <i style={{ width: pct(c.WARNING), background: "var(--warning)" }} />
        <i style={{ width: pct(c.CRITICAL), background: "var(--danger)" }} />
      </div>
      <div className="ctl-legend"><span><b>{c.OK}</b> OK</span><span><b>{c.WARNING}</b> avert.</span><span><b>{c.CRITICAL}</b> crit.</span></div>
      <div className="ctl-worst">
        {worstLines.slice(0, 2).map((w, i) => (
          <div key={i}><span className="ctl-d" style={{ background: health(w.level).color }} />{w.text}</div>
        ))}
      </div>
    </button>
  );
}

function Bar({ pct, color, height = 10 }) {
  return <div className="ctl-mini" style={{ height }}><i style={{ width: `${Math.max(0, Math.min(100, pct ?? 0))}%`, background: color }} /></div>;
}

// ── Écrans ───────────────────────────────────────────
function ScreenGlobale({ data, tk, incidents, navigate }) {
  const { nas, gateways, servers } = data;
  const groups = useMemo(() => groupByCompany({ nas, gateways, servers }), [nas, gateways, servers]);
  const active = useMemo(() => tk.tickets
    .filter(t => TICKET_ACTIVE.includes(t.status))
    .sort((a, b) => health(ticketLevel(a)).rank - health(ticketLevel(b)).rank || new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
  [tk.tickets]);
  const incCount = (co) => incidents.filter(i => i.company === co).length;
  const tkCount = (co) => active.filter(t => t.company?.name === co).length;

  const backups = useMemo(() => nas
    .flatMap(n => (n.backupJobs || []).filter(j => j.lastRunEpoch).map(j => ({ nas: n.name, ...j })))
    .sort((a, b) => b.lastRunEpoch - a.lastRunEpoch).slice(0, 8), [nas]);

  return (
    <div className="ctl-scr ctl-globale">
      <div className="ctl-panel" style={{ gridRow: "1 / 3" }}>
        <h3>Tickets prioritaires <small>{active.length} actifs</small></h3>
        {tk.loading ? <div className="ctl-empty">Chargement…</div> : active.length === 0 ? <div className="ctl-empty">Aucun ticket actif.</div> : (
          <table className="ctl-table">
            <thead><tr><th>N°</th><th>Ticket</th><th>Client</th><th>Statut</th><th>Priorité</th><th>Âge</th></tr></thead>
            <tbody>
              {active.slice(0, 12).map(t => {
                const lv = ticketLevel(t);
                return (
                  <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} style={{ cursor: "pointer" }}>
                    <td className="mono">#{t.ateraTicketId || String(t.id).substring(0, 6)}</td>
                    <td>{t.title}</td><td>{t.company?.name}</td><td>{TICKET_STATUS_LABEL[t.status] || t.status}</td>
                    <td><Tag level={lv}>{lv === "CRITICAL" ? "Critique" : lv === "WARNING" ? "Haute" : "Normale"}</Tag></td>
                    <td>{t.createdAt ? timeAgo(t.createdAt) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {active.length > 12 && <div className="ctl-more">+ {active.length - 12} autres — voir l'écran « Tickets »</div>}
      </div>

      <div className="ctl-panel">
        <h3>État par client <small>{groups.length} client{groups.length > 1 ? "s" : ""}</small></h3>
        <div className="ctl-clients">
          {groups.map(g => (
            <div key={g.company} className={`ctl-cl ${lvlKey(g.status)}`}>
              <b>{g.company}<Tag level={g.status} /></b>
              <small>{[g.gateways.length && `${g.gateways.length} UDM`, g.nas.length && `${g.nas.length} NAS`, g.servers.length && `${g.servers.length} serveur${g.servers.length > 1 ? "s" : ""}`].filter(Boolean).join(" · ")}</small>
              <small>{incCount(g.company)} incident{incCount(g.company) > 1 ? "s" : ""} · {tkCount(g.company)} ticket{tkCount(g.company) > 1 ? "s" : ""}</small>
            </div>
          ))}
        </div>
      </div>

      <div className="ctl-panel">
        <h3>Sauvegardes récentes <small>{backups.filter(b => b.status !== "OK").length} en échec</small></h3>
        {backups.length === 0 ? <div className="ctl-empty">Aucune sauvegarde détectée.</div> : (
          <table className="ctl-table"><tbody>
            {backups.map((b, i) => (
              <tr key={i}>
                <td>{b.nas}</td><td>{b.jobName}</td>
                <td><Tag level={b.status}>{b.status === "OK" ? "Réussie" : b.status === "WARNING" ? "Avert." : "Échec"}</Tag></td>
                <td style={{ color: "var(--text-3)" }}>{timeAgoEpoch(b.lastRunEpoch)}</td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>
    </div>
  );
}

const gridCols = (n) => (n <= 3 ? Math.max(1, n) : n === 4 ? 2 : n <= 6 ? 3 : 4);

/** Fournisseur(s) d'accès : plusieurs liens WAN → une pastille par FAI (noms et ASN séparés par « , »). */
function IspLine({ gateway: g }) {
  const names = (g.ispName || "").split(",").map(x => x.trim()).filter(Boolean);
  const asns = (g.ispAsn || "").split(",").map(x => x.trim()).filter(Boolean);
  return (
    <div className="ctl-isp">
      <span>{names.length > 1 ? "Fournisseurs (FAI)" : "Fournisseur (FAI)"}</span>
      {names.length === 0
        ? <em title="L'API UniFi n'a renvoyé aucun nom de FAI pour cet appareil">non détecté</em>
        : names.map((n, i) => <b key={n} title={asns[i] || undefined}>{n}{names.length === asns.length && asns[i] ? <small> {asns[i]}</small> : null}</b>)}
      {names.length > 1 && asns.length > 0 && names.length !== asns.length && <small>{asns.join(" · ")}</small>}
    </div>
  );
}

function ScreenUdm({ data }) {
  const list = useMemo(() => [...data.gateways].sort(byUrgency(deviceStatus, g => g.name)), [data.gateways]);
  if (!list.length) return <div className="ctl-empty big">Aucune passerelle enregistrée.</div>;
  const cols = gridCols(list.length);
  return (
    <div className="ctl-scr ctl-cards" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
      {list.map(g => {
        const st = deviceStatus(g); const off = st === "CRITICAL";
        return (
          <div key={g.id} className={`ctl-dev ${lvlKey(st)}`}>
            <h4>{g.name}<Tag level={st}>{off ? "Hors ligne" : g.wanStatus || health(st).label}</Tag></h4>
            <div className="ctl-co">{[g.companyName, g.model, g.publicIp && `IP ${g.publicIp}`].filter(Boolean).join(" · ")}</div>
            {g.lastError && <div className="ctl-synerr" title={g.lastError}>{g.lastError}</div>}
            <IspLine gateway={g} />
            <div className="ctl-facts">
              <div>Latence<b style={{ color: off ? "var(--danger)" : undefined }}>{off ? "Hors ligne" : g.latencyMs != null ? `${g.latencyMs} ms` : "—"}</b></div>
              <div>Perte de paquets<b>{g.packetLossPercent != null ? `${g.packetLossPercent} %` : "—"}</b></div>
              <div>Disponibilité<b>{g.uptimePercent != null ? `${g.uptimePercent} %` : "—"}</b></div>
              <div>Clients<b>{g.connectedClients ?? "—"}</b></div>
              <div>↓ / ↑<b className="sm">{g.downMbps != null ? `${g.downMbps} / ${g.upMbps ?? "—"} Mbps` : "—"}</b></div>
              <div>Dernier contact<b className="sm">{g.lastSeenAt ? timeAgo(g.lastSeenAt) : "jamais"}</b></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScreenNas({ data }) {
  const list = useMemo(() => [...data.nas].sort(byUrgency(deviceStatus, n => n.name)), [data.nas]);
  if (!list.length) return <div className="ctl-empty big">Aucun NAS enregistré.</div>;
  const cols = gridCols(list.length);
  return (
    <div className="ctl-scr ctl-cards" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))` }}>
      {list.map(n => {
        const st = deviceStatus(n); const pct = n.storagePercent;
        const raidBad = n.raidStatus && n.raidStatus.toLowerCase() !== "normal";
        const jobs = n.backupJobs || [];
        return (
          <div key={n.id} className={`ctl-dev ${lvlKey(st)}`}>
            <h4>{n.name}<Tag level={st} /></h4>
            <div className="ctl-co">{[n.companyName, n.model].filter(Boolean).join(" · ")}</div>
            {n.lastError && <div className="ctl-synerr" title={n.lastError}>{n.lastError}</div>}
            <div>
              <div className="ctl-row"><span>Stockage</span><b style={{ color: pct != null ? storageColor(pct) : undefined }}>{pct != null ? `${pct} %` : "—"}</b></div>
              <Bar pct={pct} color={pct != null ? storageColor(pct) : "var(--text-3)"} height={12} />
            </div>
            <div className="ctl-facts" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
              <div>RAID<b style={{ color: raidBad ? "var(--danger)" : undefined }}>{n.raidStatus || "—"}</b></div>
              <div>Température<b>{n.temperatureC != null ? `${n.temperatureC} °C` : "—"}</b></div>
              <div>Capacité<b className="sm">{formatStorage(n.storageUsedGb)} / {formatStorage(n.storageTotalGb)}</b></div>
            </div>
            {(n.disks || []).length > 0 && (
              <div className="ctl-disks">Disques {(n.disks || []).map((d, i) => <span key={i} title={`Baie ${d.bay ?? i + 1}`} style={{ background: health(d.status).color }} />)}</div>
            )}
            <div className="ctl-bk">
              {jobs.length ? <><LastBackupLine jobs={jobs} /><BackupJobList jobs={[...jobs].sort((a, b) => health(a.status).rank - health(b.status).rank).slice(0, 3)} />{jobs.length > 3 && <div className="ctl-muted">+ {jobs.length - 3} autre{jobs.length - 3 > 1 ? "s" : ""} tâche{jobs.length - 3 > 1 ? "s" : ""}</div>}</> : <span className="ctl-muted">Aucune tâche de sauvegarde détectée.</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScreenServeurs({ data }) {
  const list = useMemo(() => [...data.servers].sort(byUrgency(serverStatus, s => s.hostname)), [data.servers]);
  if (!list.length) return <div className="ctl-empty big">Aucun serveur supervisé.</div>;
  return (
    <div className="ctl-scr" style={{ display: "block" }}>
      <div className="ctl-panel">
        <h3>Serveurs <small>{list.length} supervisés · {list.filter(s => serverStatus(s) === "CRITICAL").length} critiques</small></h3>
        <table className="ctl-table">
          <thead><tr><th /><th>Serveur</th><th>Client</th><th>Système</th><th>Processeur</th><th>RAM</th><th>Disque</th><th>Session</th><th>Agent</th></tr></thead>
          <tbody>
            {list.map(s => {
              const st = serverStatus(s); const online = s.status === "ONLINE"; const d = diskInfo(s);
              return (
                <tr key={s.id} className={lvlKey(st)}>
                  <td><span className="ctl-d" style={{ background: health(st).color }} /></td>
                  <td><b>{s.hostname || "—"}</b></td><td>{s.companyName}</td>
                  <td className="muted">{s.os || "—"}</td><td className="muted">{s.cpu || "—"}</td>
                  <td>{s.ramGb ? `${s.ramGb} Go` : "—"}</td>
                  <td>{d ? <div className="ctl-diskcell"><Bar pct={d.pct} color={d.color} /><span>{d.pct} %</span></div> : "—"}</td>
                  <td className="muted">{s.currentLoggedUser || "—"}</td>
                  <td style={{ color: online ? "var(--text-2)" : "var(--danger)" }}>{s.lastSeenAt ? (online ? timeAgo(s.lastSeenAt) : `hors ligne · ${timeAgo(s.lastSeenAt)}`) : "jamais connecté"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScreenTickets({ tk, navigate }) {
  const cols = [["CRITICAL", "Critiques"], ["WARNING", "Hauts"], ["OK", "Normaux"]];
  const active = useMemo(() => tk.tickets.filter(t => TICKET_ACTIVE.includes(t.status))
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)), [tk.tickets]);
  const boxRef = useRef(null);
  const [limit, setLimit] = useState(8);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const fit = () => setLimit(Math.max(3, Math.floor((el.clientHeight - 100) / 66)));   // ≈ 66 px par carte, on garde la place du « + N autres »
    fit();
    const ro = new ResizeObserver(fit); ro.observe(el);
    return () => ro.disconnect();
  }, [tk.loading]);
  if (tk.loading) return <div className="ctl-empty big">Chargement des tickets…</div>;
  if (tk.error) return <div className="ctl-empty big" style={{ color: "var(--danger)" }}>{tk.error}</div>;
  return (
    <div ref={boxRef} className="ctl-scr ctl-kanban">
      {cols.map(([lv, title]) => {
        const list = active.filter(t => ticketLevel(t) === lv);
        return (
          <div key={lv} className={`ctl-kan ${lvlKey(lv)}`}>
            <h4><span className="ctl-d" style={{ background: health(lv).color }} />{title}<span>{list.length}</span></h4>
            {list.length === 0 && <div className="ctl-empty">Aucun.</div>}
            {list.slice(0, limit).map(t => {
              const late = ticketSla(t) === "late";
              return (
                <div key={t.id} className={`ctl-tk ${lvlKey(lv)}`} onClick={() => navigate(`/tickets/${t.id}`)}>
                  <b>#{t.ateraTicketId || String(t.id).substring(0, 6)} {t.title}</b>
                  <small>{[t.company?.name, TICKET_STATUS_LABEL[t.status]].filter(Boolean).join(" · ")}<em>{late ? "Délai dépassé · " : ""}{t.createdAt ? timeAgo(t.createdAt) : ""}</em></small>
                </div>
              );
            })}
            {list.length > limit && <div className="ctl-more">+ {list.length - limit} autre{list.length - limit > 1 ? "s" : ""}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ── Onglet ───────────────────────────────────────────
export default function ControleTab({ data, onOpenDevice }) {
  const navigate = useNavigate();
  const { nas, gateways, servers, loading, refreshing, lastRefresh, errors, reload, intervalMs } = data;
  const tk = useTicketList();

  const [screen, setScreenState] = useState(readScreen);
  const [auto, setAuto] = useState(false);
  const [tv, setTv] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [tile, setTile] = useState(272);       // côté des tuiles (suit la largeur disponible)
  const [scale, setScale] = useState(1);       // agrandissement en mode TV
  const rootRef = useRef(null);
  const fsRef = useRef(null);

  const setScreen = useCallback((id) => {
    setScreenState(id);
    try { sessionStorage.setItem(STORAGE_KEY, id); } catch { /* sans conséquence */ }
  }, []);

  // ── Lecture automatique : fait défiler les 5 écrans TV ──
  useEffect(() => {
    if (!auto) return;
    const id = setTimeout(() => {
      if (document.hidden) { setScreenState(s => s); return; }
      const i = TV_IDS.indexOf(screen);
      setScreen(TV_IDS[(i + 1) % TV_IDS.length]);     // depuis une vue de bureau : retombe sur l'écran 1
    }, ROTATION_MS);
    return () => clearTimeout(id);
  }, [auto, screen, setScreen]);

  // ── Mode TV : plein écran + agrandissement ──
  useEffect(() => {
    const onFs = () => { if (!document.fullscreenElement) setTv(false); };
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const enterTv = () => {
    setTv(true);
    setAuto(true);
    if (!TV_IDS.includes(screen)) setScreen("globale");
    try { fsRef.current?.requestFullscreen?.().catch(() => {}); } catch { /* plein écran refusé : le mode TV reste actif dans la page */ }
  };
  const leaveTv = () => {
    setTv(false);
    try { if (document.fullscreenElement) document.exitFullscreen(); } catch { /* ignoré */ }
  };

  // ── Dimensions : côté des tuiles et échelle TV ──
  useEffect(() => {
    const measure = () => {
      const w = window.innerWidth, h = window.innerHeight;
      const s = tv ? Math.min(2.5, Math.max(0.7, Math.min(w / 1920, h / 1080))) : 1;
      setScale(s);
      const avail = (rootRef.current?.parentElement?.clientWidth || w) / (tv ? s : 1);
      setTile(Math.round(Math.min(272, Math.max(168, avail * 0.145))));
    };
    measure();
    window.addEventListener("resize", measure);
    const ro = typeof ResizeObserver !== "undefined" && rootRef.current?.parentElement ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(rootRef.current.parentElement);
    return () => { window.removeEventListener("resize", measure); ro?.disconnect(); };
  }, [tv]);

  const handleReload = () => { reload(); tk.reload(); };

  // ── Données dérivées ──
  const incidents = useMemo(() => buildIncidents({ nas, gateways, servers })
    .sort((a, b) => health(a.level).rank - health(b.level).rank), [nas, gateways, servers]);
  const lateTickets = useMemo(() => tk.tickets.filter(t => ticketSla(t) === "late")
    .sort((a, b) => health(ticketLevel(a)).rank - health(ticketLevel(b)).rank), [tk.tickets]);

  const todo = useMemo(() => [
    ...incidents.map(i => ({ key: i.key, level: i.level, who: i.company, text: `${i.device} — ${i.title}`, go: () => onOpenDevice?.(i.kind, i.id) })),
    ...lateTickets.map(t => ({ key: `t${t.id}`, level: ticketLevel(t), who: t.company?.name, text: `Ticket #${t.ateraTicketId || String(t.id).substring(0, 6)} en retard — ${t.title}`, go: () => navigate(`/tickets/${t.id}`) })),
  ].sort((a, b) => health(a.level).rank - health(b.level).rank), [incidents, lateTickets, onOpenDevice, navigate]);

  const clientGroups = useMemo(() => groupByCompany({ nas, gateways, servers }), [nas, gateways, servers]);
  const clientCount = countStatuses(clientGroups.map(g => g.status));
  const worstOf = (kind) => incidents.filter(i => i.kind === kind).map(i => ({ level: i.level, text: `${i.device} — ${i.title}` }));
  const error = [errors.monitoring, errors.servers].filter(Boolean).join(" · ");
  const isTvScreen = TV_IDS.includes(screen);
  const todoMax = tile >= 230 ? 5 : tile >= 190 ? 4 : 3;

  const wrapStyle = tv
    ? { zoom: scale, width: window.innerWidth / scale, height: window.innerHeight / scale }
    : {};

  return (
    <div ref={fsRef} className={tv ? "ctl-fs" : ""}>
      <div ref={rootRef} className={`ctl-root${tv ? " tv" : ""}`} style={{ ...wrapStyle, "--t": `${tile}px` }}>
        {/* En-tête */}
        <div className="ctl-head">
          <div>
            <div className="ctl-title">Tour de <span>contrôle</span></div>
            <div className="ctl-sub">Tickets, UDM, NAS & serveurs</div>
          </div>
          <div className="ctl-nav" role="tablist" aria-label="Écrans">
            {SCREENS.map((s, i) => (
              <button key={s.id} role="tab" aria-selected={screen === s.id}
                className={`${screen === s.id ? "act" : ""}${!s.tv && SCREENS[i - 1]?.tv ? " sep" : ""}`}
                onClick={() => setScreen(s.id)}>
                {s.label}
                {auto && screen === s.id && <i key={screen} className="ctl-pg" />}
              </button>
            ))}
          </div>
          <div className="ctl-tools">
            <span className="ctl-legendchips"><Chip color="var(--success)">● Opérationnel</Chip><Chip color="var(--warning)">● Avertissement</Chip><Chip color="var(--danger)">● Critique</Chip></span>
            {!tv && <LiveIndicator lastRefresh={lastRefresh} refreshing={refreshing} intervalMs={intervalMs} onRefresh={handleReload} />}
            {tv && <LiveClock compact />}
            <button type="button" className={`ctl-btn${auto ? " on" : ""}`} onClick={() => setAuto(a => !a)} title="Fait défiler les 5 écrans toutes les 15 secondes">
              <i className={`bi ${auto ? "bi-pause-fill" : "bi-play-fill"}`} /> {auto ? "Pause" : "Lecture auto"}
            </button>
            <button type="button" className="ctl-btn" onClick={tv ? leaveTv : enterTv} title={tv ? "Quitter le mode TV" : "Plein écran, lecture automatique, grande taille"}>
              <i className={`bi ${tv ? "bi-fullscreen-exit" : "bi-fullscreen"}`} /> {tv ? "Quitter" : "Mode TV"}
            </button>
          </div>
        </div>

        {/* Bandeau d'incidents défilant */}
        {incidents.length > 0 && (
          <div className="ctl-ticker" role="status" aria-label="Incidents actifs">
            <div className="ctl-ticker-label"><i className="bi bi-exclamation-triangle" /> Incidents actifs ({incidents.length})</div>
            <div className="ctl-ticker-view">
              <div className="ctl-ticker-track">
                {[0, 1].map(k => (
                  <span key={k} className="ctl-ticker-set" aria-hidden={k === 1}>
                    {incidents.map(i => (
                      <span key={`${k}-${i.key}`} className="ctl-ticker-item" onClick={() => onOpenDevice?.(i.kind, i.id)}>
                        <span style={{ color: health(i.level).color }}>{i.level === "CRITICAL" ? "●" : "▲"}</span> {i.company ? `${i.company} — ` : ""}{i.device} : {i.title}
                      </span>
                    ))}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Indicateurs par client */}
        <div className="ctl-kpis">
          <div className="ctl-kpi"><span>Clients surveillés</span><b>{loading ? "…" : clientGroups.length}</b></div>
          <div className="ctl-kpi"><span>Opérationnels</span><b style={{ color: "var(--success)" }}>{loading ? "…" : clientCount.OK}</b></div>
          <div className="ctl-kpi"><span>Avertissements</span><b style={{ color: "var(--warning)" }}>{loading ? "…" : clientCount.WARNING}</b></div>
          <div className="ctl-kpi"><span>Critiques</span><b style={{ color: "var(--danger)" }}>{loading ? "…" : clientCount.CRITICAL}</b></div>
        </div>

        {/* Rangée fixe : 3 tuiles + à traiter */}
        <div className="ctl-row1">
          <Tile icon="bi-router" title="UDM" caption="à surveiller" statuses={gateways.map(deviceStatus)} worstLines={worstOf("udm")} active={screen === "udm"} onClick={() => setScreen("udm")} />
          <Tile icon="bi-hdd-network" title="NAS" caption="à surveiller" statuses={nas.map(deviceStatus)} worstLines={worstOf("nas")} active={screen === "nas"} onClick={() => setScreen("nas")} />
          <Tile icon="bi-pc-display-horizontal" title="Serveurs" caption="à surveiller" statuses={servers.map(serverStatus)} worstLines={worstOf("server")} active={screen === "serveurs"} onClick={() => setScreen("serveurs")} />
          <div className="ctl-feed">
            <h3>À traiter maintenant <span className={`ctl-pill ${todo.length ? lvlKey(todo[0].level) : "ok"}`}>{loading ? "…" : todo.length || "RAS"}</span></h3>
            {!loading && todo.length === 0 ? (
              <div className="ctl-calm"><i className="bi bi-check-circle-fill" /><b>Tout est opérationnel</b></div>
            ) : (
              <ul>
                {todo.slice(0, todoMax).map(t => (
                  <li key={t.key} className={lvlKey(t.level)} onClick={t.go} role="button" tabIndex={0}>
                    <span className="who">{t.who || "—"}</span><span className="txt">{t.text}</span><span className="go">Ouvrir ›</span>
                  </li>
                ))}
                {todo.length > todoMax && <li className="more">+ {todo.length - todoMax} autre{todo.length - todoMax > 1 ? "s" : ""}</li>}
              </ul>
            )}
          </div>
        </div>

        {error && <div className="ctl-err">{error}</div>}

        {/* Grande zone */}
        <div className={`ctl-zone${isTvScreen ? "" : " desk"}`}>
          {loading && isTvScreen && screen !== "tickets" && screen !== "globale" ? <div className="ctl-empty big">Chargement…</div> : null}
          {screen === "globale"  && <ScreenGlobale data={data} tk={tk} incidents={incidents} navigate={navigate} />}
          {screen === "udm"      && !loading && <ScreenUdm data={data} />}
          {screen === "nas"      && !loading && <ScreenNas data={data} />}
          {screen === "serveurs" && !loading && <ScreenServeurs data={data} />}
          {screen === "tickets"  && <ScreenTickets tk={tk} navigate={navigate} />}
          {screen === "colonnes" && (
            <>
              <div style={{ ...segStyle, marginBottom: 12 }} role="group" aria-label="Affichage">
                <button style={segBtn(!expandAll)} aria-pressed={!expandAll} onClick={() => setExpandAll(false)}>Compact</button>
                <button style={segBtn(expandAll)} aria-pressed={expandAll} onClick={() => setExpandAll(true)}>Tout déplier</button>
              </div>
              <ColumnsBoard data={data} tk={tk} expandAll={expandAll} />
            </>
          )}
          {screen === "clients" && (
            <Section loading={loading} error={error} count={groupByCompany({ nas, gateways, servers }).length} empty="Aucun appareil supervisé pour le moment.">
              <CardGrid min={340}>{groupByCompany({ nas, gateways, servers }).map(g => <ClientCard key={g.company} group={g} />)}</CardGrid>
            </Section>
          )}
        </div>
      </div>
      <style>{CSS}</style>
    </div>
  );
}

const CSS = `
.ctl-fs { background: var(--navy-mid); overflow: hidden; }
.ctl-root { display: flex; flex-direction: column; gap: 16px; contain: inline-size; min-width: 0; height: calc(100vh - 165px); min-height: 640px; }
.ctl-root.tv { padding: 22px 34px 18px; height: auto; min-height: 0; background: var(--navy-mid); contain: none; overflow: hidden; }
.ctl-root .ok   { --c: var(--success); --cd: var(--success-dim); }
.ctl-root .warn { --c: var(--warning); --cd: var(--warning-dim); }
.ctl-root .crit { --c: var(--danger);  --cd: var(--danger-dim); }

/* En-tête */
.ctl-head { display: flex; align-items: center; gap: 18px; flex: none; flex-wrap: wrap; }
.ctl-title { font-family: Syne, sans-serif; font-weight: 800; font-size: 26px; color: var(--text-1); line-height: 1.1; }
.ctl-title span { color: var(--accent); }
.ctl-sub { font-size: 12.5px; color: var(--text-3); margin-top: 2px; }
.ctl-nav { display: flex; gap: 6px; flex-wrap: wrap; margin: 0 auto; }
.ctl-nav button { position: relative; overflow: hidden; font: inherit; font-weight: 600; font-size: 13.5px; cursor: pointer; padding: 8px 15px; border-radius: 11px;
  border: 1px solid var(--border-md); background: var(--card-bg); color: var(--text-2); }
.ctl-nav button.act { color: var(--text-1); border-color: var(--accent); }
.ctl-nav button.sep { margin-left: 14px; }
.ctl-pg { position: absolute; left: 0; bottom: 0; height: 3px; background: var(--accent); animation: ctlPg ${ROTATION_MS}ms linear forwards; }
@keyframes ctlPg { from { width: 0 } to { width: 100% } }
.ctl-tools { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.ctl-btn { font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; padding: 7px 12px; border-radius: 10px; border: 1px solid var(--border-md); background: var(--card-bg); color: var(--text-2); display: inline-flex; align-items: center; gap: 6px; }
.ctl-btn.on { color: var(--accent); border-color: var(--accent); }
.ctl-btn:hover { background: var(--navy-hover); }

/* Bandeau + indicateurs */
.ctl-legendchips { display: inline-flex; gap: 6px; flex-wrap: wrap; }
.ctl-ticker { display: flex; align-items: stretch; flex: none; border: 1px solid var(--danger); background: var(--danger-dim); border-radius: 10px; overflow: hidden; min-width: 0; }
.ctl-ticker-label { background: var(--danger-solid, var(--danger)); color: #fff; font: 700 12px/1 system-ui; padding: 0 14px; display: flex; align-items: center; gap: 7px; white-space: nowrap; }
.ctl-ticker-view { flex: 1; min-width: 0; overflow: hidden; padding: 9px 0; }
.ctl-ticker-track { display: inline-flex; white-space: nowrap; animation: ctlTick 60s linear infinite; }
.ctl-ticker:hover .ctl-ticker-track { animation-play-state: paused; }
.ctl-ticker-set { display: inline-flex; }
.ctl-ticker-item { font-size: 13px; color: var(--text-1); padding: 0 22px; cursor: pointer; }
@keyframes ctlTick { from { transform: translateX(0) } to { transform: translateX(-50%) } }
.ctl-kpis { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; flex: none; }
.ctl-kpi { background: var(--card-bg); border: 1px solid var(--border); border-radius: 12px; padding: 8px 16px; display: flex; align-items: baseline; justify-content: space-between; gap: 10px; min-width: 0; }
.ctl-kpi span { font-size: 12.5px; color: var(--text-3); }
.ctl-kpi b { font-family: Syne, sans-serif; font-size: 26px; line-height: 1.1; color: var(--text-1); }

/* Rangée fixe */
.ctl-row1 { display: flex; gap: 16px; height: var(--t); flex: none; }
.ctl-tile { width: var(--t); height: var(--t); flex: none; background: var(--card-bg); border: 1px solid var(--border); border-radius: 18px; padding: calc(var(--t) * .07) calc(var(--t) * .08);
  display: flex; flex-direction: column; position: relative; overflow: hidden; cursor: pointer; text-align: left; font: inherit; color: inherit; transition: transform .12s; }
.ctl-tile:hover { transform: translateY(-2px); }
.ctl-tile::before { content: ""; position: absolute; left: 0; top: 0; right: 0; height: 6px; background: var(--c); }
.ctl-tile.sel { outline: 2px solid var(--accent); outline-offset: -2px; }
.ctl-tile h3 { font-family: Syne, sans-serif; font-size: calc(var(--t) * .074); display: flex; align-items: center; gap: 9px; margin: 0; color: var(--text-1); }
.ctl-tile h3 i { width: calc(var(--t) * .118); height: calc(var(--t) * .118); border-radius: 9px; display: grid; place-items: center; background: var(--navy-soft); font-size: calc(var(--t) * .06); }
.ctl-big { display: flex; align-items: baseline; gap: 9px; margin: calc(var(--t) * .04) 0 2px; }
.ctl-big b { font-family: Syne, sans-serif; font-size: calc(var(--t) * .28); line-height: 1; color: var(--c); }
.ctl-big span { color: var(--text-2); font-size: calc(var(--t) * .059); line-height: 1.2; }
.ctl-bar3 { display: flex; height: 10px; border-radius: 99px; overflow: hidden; background: var(--navy-soft); margin: 8px 0 8px; flex: none; }
.ctl-bar3 i { display: block; height: 100%; }
.ctl-legend { display: flex; gap: 10px; font-size: calc(var(--t) * .05); color: var(--text-2); flex: none; }
.ctl-legend b { color: var(--text-1); }
.ctl-worst { margin-top: auto; font-size: calc(var(--t) * .05); color: var(--text-2); display: flex; flex-direction: column; gap: 4px; min-height: 0; }
.ctl-worst div { display: flex; align-items: center; gap: 7px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ctl-d { width: 9px; height: 9px; border-radius: 50%; flex: none; display: inline-block; }

.ctl-feed { flex: 1; min-width: 0; background: var(--card-bg); border: 1px solid var(--border); border-radius: 18px; padding: 14px 20px; display: flex; flex-direction: column; overflow: hidden; }
.ctl-feed h3 { font-family: Syne, sans-serif; font-size: 18px; display: flex; align-items: center; gap: 10px; margin: 0 0 8px; color: var(--text-1); }
.ctl-pill { font: 700 12px/1 system-ui; padding: 5px 9px; border-radius: 99px; background: var(--cd); color: var(--c); }
.ctl-feed ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 6px; min-height: 0; }
.ctl-feed li { display: flex; align-items: center; gap: 12px; padding: 7px 12px; border-radius: 11px; font-size: 14px; border-left: 5px solid var(--c); background: var(--cd); cursor: pointer; color: var(--text-1); }
.ctl-feed li .who { font-weight: 800; min-width: 84px; max-width: 150px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ctl-feed li .txt { min-width: 0; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ctl-feed li .go { font: 700 11.5px system-ui; padding: 4px 9px; border-radius: 7px; background: var(--navy-soft); color: var(--text-1); flex: none; }
.ctl-feed li.more { background: none; border-left-color: transparent; color: var(--text-3); cursor: default; padding: 2px 12px; font-size: 13px; }
.ctl-calm { flex: 1; display: flex; align-items: center; justify-content: center; gap: 16px; color: var(--success); }
.ctl-calm i { font-size: 44px; } .ctl-calm b { font-family: Syne, sans-serif; font-size: 28px; }
.ctl-err { flex: none; font-size: 13px; color: var(--danger); background: var(--danger-dim); border-radius: 10px; padding: 8px 12px; }

/* Grande zone */
.ctl-zone { flex: 1; min-height: 0; overflow: hidden; display: flex; flex-direction: column; }
.ctl-zone.desk { overflow: auto; display: block; }
.ctl-scr { flex: 1; min-height: 0; display: grid; gap: 16px; animation: ctlIn .3s ease; }
@keyframes ctlIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.ctl-globale { grid-template-columns: 1.25fr 1fr; grid-template-rows: minmax(0,1fr) minmax(0,1fr); }
.ctl-cards { grid-auto-rows: minmax(0, 1fr); }
.ctl-kanban { grid-template-columns: repeat(3, minmax(0,1fr)); }
.ctl-panel { background: var(--card-bg); border: 1px solid var(--border); border-radius: 18px; padding: 16px 20px; display: flex; flex-direction: column; min-height: 0; min-width: 0; overflow: hidden; }
.ctl-panel h3 { font-family: Syne, sans-serif; font-size: 18px; display: flex; align-items: center; gap: 10px; margin: 0 0 8px; flex: none; color: var(--text-1); }
.ctl-panel h3 small { font: 500 13px "DM Sans", sans-serif; color: var(--text-3); margin-left: auto; }
.ctl-table { width: 100%; border-collapse: collapse; font-size: 14px; color: var(--text-1); }
.ctl-table th { text-align: left; font-size: 11px; letter-spacing: .06em; text-transform: uppercase; color: var(--text-3); padding: 0 8px 7px; font-weight: 600; }
.ctl-table td { padding: 7px 8px; border-top: 1px solid var(--border); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 360px; }
.ctl-table td.mono { font-family: monospace; color: var(--text-3); }
.ctl-table td.muted { color: var(--text-2); }
.ctl-table tbody tr:hover { background: var(--navy-hover); }
.ctl-tag { font: 700 11px/1 system-ui; padding: 4px 8px; border-radius: 99px; background: var(--cd); color: var(--c); white-space: nowrap; }
.ctl-more { text-align: center; color: var(--accent); font-weight: 600; font-size: 13px; padding: 10px 0 2px; }
.ctl-empty { color: var(--text-3); font-size: 14px; padding: 14px; text-align: center; }
.ctl-empty.big { font-size: 18px; padding: 60px 0; }
.ctl-clients { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 10px; overflow: hidden; }
.ctl-cl { border: 1px solid var(--border); border-left: 5px solid var(--c); border-radius: 12px; padding: 10px 12px; background: var(--navy-mid); min-width: 0; }
.ctl-cl b { font-size: 15px; display: flex; justify-content: space-between; align-items: center; gap: 8px; color: var(--text-1); }
.ctl-cl small { display: block; color: var(--text-2); font-size: 12px; margin-top: 3px; }
.ctl-dev { background: var(--card-bg); border: 1px solid var(--border); border-top: 6px solid var(--c); border-radius: 16px; padding: 14px 18px; display: flex; flex-direction: column; gap: 8px; min-height: 0; min-width: 0; overflow: hidden; }
.ctl-dev h4 { font-family: Syne, sans-serif; font-size: 18px; display: flex; align-items: center; gap: 10px; margin: 0; color: var(--text-1); }
.ctl-dev h4 .ctl-tag { margin-left: auto; }
.ctl-co { color: var(--text-3); font-size: 13px; line-height: 1.35; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ctl-facts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 14px; }
.ctl-facts div { font-size: 12.5px; color: var(--text-3); min-width: 0; }
.ctl-facts b { display: block; font-size: 18px; color: var(--text-1); font-weight: 700; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ctl-facts b.sm { font-size: 14.5px; }
.ctl-row { display: flex; justify-content: space-between; font-size: 13px; color: var(--text-2); margin-bottom: 4px; }
.ctl-mini { border-radius: 99px; background: var(--navy-soft); overflow: hidden; min-width: 70px; flex: 1; }
.ctl-mini i { display: block; height: 100%; }
.ctl-disks { display: flex; gap: 6px; align-items: center; font-size: 12.5px; color: var(--text-3); }
.ctl-disks span { width: 30px; height: 12px; border-radius: 4px; display: inline-block; }
.ctl-bk { min-height: 0; overflow: hidden; }
.ctl-isp { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; font-size: 12.5px; color: var(--text-3); }
.ctl-isp b { font-size: 14px; color: var(--text-1); background: var(--navy-soft); border-radius: 99px; padding: 3px 11px; font-weight: 700; }
.ctl-isp b small, .ctl-isp > small { font-weight: 500; color: var(--text-3); font-size: 11.5px; }
.ctl-isp em { font-style: normal; color: var(--text-3); }
.ctl-synerr { font-size: 12.5px; color: var(--danger); background: var(--danger-dim); border-radius: 8px; padding: 5px 9px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ctl-muted { font-size: 12.5px; color: var(--text-3); }
.ctl-diskcell { display: flex; align-items: center; gap: 10px; min-width: 130px; }
.ctl-diskcell span { width: 44px; text-align: right; }
.ctl-kan { border-radius: 16px; background: var(--navy-soft); padding: 12px; display: flex; flex-direction: column; gap: 8px; min-height: 0; border-top: 5px solid var(--c); overflow: hidden; }
.ctl-kan h4 { font-family: Syne, sans-serif; font-size: 17px; display: flex; align-items: center; gap: 9px; margin: 0; padding: 2px 4px; color: var(--text-1); }
.ctl-kan h4 span:last-child { margin-left: auto; font-size: 14px; color: var(--c); }
.ctl-tk { background: var(--card-bg); border: 1px solid var(--border); border-left: 5px solid var(--c); border-radius: 11px; padding: 8px 12px; cursor: pointer; color: var(--text-1); }
.ctl-tk b { display: block; font-size: 13.5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ctl-tk small { color: var(--text-2); font-size: 12px; display: flex; gap: 8px; }
.ctl-tk small em { font-style: normal; margin-left: auto; color: var(--c); font-weight: 600; white-space: nowrap; }
@media (prefers-reduced-motion: reduce) { .ctl-scr, .ctl-pg, .ctl-ticker-track { animation: none; } }
@media (max-width: 1100px) { .ctl-root { height: auto; } .ctl-row1 { height: auto; flex-wrap: wrap; } .ctl-tile { height: var(--t); } .ctl-feed { flex-basis: 100%; }
  .ctl-globale, .ctl-kanban { grid-template-columns: 1fr; grid-template-rows: none; } .ctl-zone { overflow: visible; } }
`;